// Client-side detection + repair for the OUTER RING of a GeoJSON Polygon.
// Coordinates are [lng, lat]. No extra dependencies: the segment-intersection
// loop mirrors @turf/kinks but keeps the offending edge/vertex indices so the
// UI can pinpoint *where* a problem is and offer a targeted fix.
//
// `polygon-validation.ts` stays the strict save-gate; this module powers the
// live "what is wrong and how do I fix it" experience in the map editor.

export type PolygonIssueType =
  | "min_points"
  | "duplicate"
  | "self_intersection"
  | "spike"
  | "zero_area";

export type PolygonFix = "remove_duplicates" | "remove_vertex" | "auto_repair";

export interface PolygonIssue {
  type: PolygonIssueType;
  /** i18n key describing the issue. */
  message: string;
  /** Vertices to highlight, as indices into the OPEN ring. */
  vertexIndices: number[];
  /** Coordinates to highlight. For self_intersection the FIRST entry is the
   *  crossing point [lng,lat]; the rest are the involved vertices. */
  coordinates: number[][];
  /** Suggested one-click fix, if any. */
  fix?: PolygonFix;
}

type Pt = number[]; // [lng, lat]

const EPS_COLLINEAR = 1e-9; // cross-product tolerance for spikes
const NEAR_DUP_METERS = 0.5; // near-duplicate threshold (0 disables)
const ZERO_AREA_EPSILON_M2 = 1;

const same = (a?: Pt, b?: Pt) => !!a && !!b && a[0] === b[0] && a[1] === b[1];

/** Strip the optional closing point so indices are stable and unambiguous. */
export const toOpenRing = (ring: Pt[]): Pt[] =>
  ring.length > 1 && same(ring[0], ring[ring.length - 1])
    ? ring.slice(0, -1)
    : ring.slice();

/** Approximate ground distance (m) between two [lng,lat] points. */
const distMeters = (a: Pt, b: Pt): number => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat = toRad((a[1] + b[1]) / 2);
  const x = dLng * Math.cos(lat);
  return Math.sqrt(x * x + dLat * dLat) * R;
};

/**
 * Parametric segment intersection (same math as @turf/kinks `lineIntersects`).
 * Returns the crossing point [lng,lat] when the two SEGMENTS cross, else null.
 */
const segmentIntersection = (p1: Pt, p2: Pt, p3: Pt, p4: Pt): Pt | null => {
  const denom =
    (p4[1] - p3[1]) * (p2[0] - p1[0]) - (p4[0] - p3[0]) * (p2[1] - p1[1]);
  if (denom === 0) return null; // parallel / collinear
  const a = p1[1] - p3[1];
  const b = p1[0] - p3[0];
  const n1 = (p4[0] - p3[0]) * a - (p4[1] - p3[1]) * b;
  const n2 = (p2[0] - p1[0]) * a - (p2[1] - p1[1]) * b;
  const ua = n1 / denom;
  const ub = n2 / denom;
  if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
    return [p1[0] + ua * (p2[0] - p1[0]), p1[1] + ua * (p2[1] - p1[1])];
  }
  return null;
};

export interface SelfIntersection {
  edgeA: number; // start-vertex index of edge i -> i+1
  edgeB: number; // start-vertex index of edge k -> k+1
  vertexIndices: number[]; // the 4 vertices of the two crossing edges
  point: Pt; // crossing point [lng, lat]
}

/**
 * All self-intersections with their edge/vertex indices (kept, unlike kinks).
 */
export const findSelfIntersections = (ring: Pt[]): SelfIntersection[] => {
  const o = toOpenRing(ring);
  const n = o.length;
  const out: SelfIntersection[] = [];
  if (n < 4) return out; // a triangle cannot self-cross
  for (let i = 0; i < n; i++) {
    for (let k = i + 1; k < n; k++) {
      if (k === i + 1) continue; // adjacent edges share a vertex
      if (i === 0 && k === n - 1) continue; // first & last edges are adjacent
      const pt = segmentIntersection(
        o[i],
        o[(i + 1) % n],
        o[k],
        o[(k + 1) % n],
      );
      if (pt) {
        out.push({
          edgeA: i,
          edgeB: k,
          vertexIndices: [i, (i + 1) % n, k, (k + 1) % n],
          point: pt,
        });
      }
    }
  }
  return out;
};

/** Exact + near-duplicate consecutive vertices, by index. */
export const findDuplicatePoints = (ring: Pt[]): number[] => {
  const o = toOpenRing(ring);
  const n = o.length;
  const dupIdx = new Set<number>();
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const exact = same(o[i], o[j]);
    const near =
      NEAR_DUP_METERS > 0 && !exact && distMeters(o[i], o[j]) <= NEAR_DUP_METERS;
    if (exact || near) dupIdx.add(j); // the later one is redundant
  }
  return Array.from(dupIdx);
};

/** Spikes / redundant collinear vertices (cleanCoords-style 3-point test). */
export const findSpikes = (ring: Pt[]): number[] => {
  const o = toOpenRing(ring);
  const n = o.length;
  if (n < 4) return [];
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = o[(i - 1 + n) % n];
    const b = o[i];
    const c = o[(i + 1) % n];
    const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    if (Math.abs(cross) <= EPS_COLLINEAR) out.push(i); // b lies on line a-c
  }
  return out;
};

/** Shoelace area (m²) in a local equirectangular projection, no turf needed. */
const polygonAreaSqMeters = (ring: Pt[]): number => {
  const o = toOpenRing(ring);
  if (o.length < 3) return 0;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const lat0 = toRad(o.reduce((s, p) => s + p[1], 0) / o.length);
  const xy = o.map((p) => [R * toRad(p[0]) * Math.cos(lat0), R * toRad(p[1])]);
  let a = 0;
  for (let i = 0; i < xy.length; i++) {
    const j = (i + 1) % xy.length;
    a += xy[i][0] * xy[j][1] - xy[j][0] * xy[i][1];
  }
  return Math.abs(a) / 2;
};

/**
 * Every distinct issue with the indices + coordinates to highlight and a
 * suggested fix. Drives the live diagnostics panel in the map editor.
 */
export const detectPolygonIssues = (ring?: Pt[] | null): PolygonIssue[] => {
  if (!Array.isArray(ring) || ring.length === 0) return [];
  const issues: PolygonIssue[] = [];
  const o = toOpenRing(ring);

  if (o.length < 3) {
    issues.push({
      type: "min_points",
      message: "polygon_min_points",
      vertexIndices: o.map((_, i) => i),
      coordinates: o.slice(),
    });
    return issues;
  }

  const dups = findDuplicatePoints(o);
  if (dups.length) {
    issues.push({
      type: "duplicate",
      message: "polygon_duplicate_points",
      vertexIndices: dups,
      coordinates: dups.map((i) => o[i]),
      fix: "remove_duplicates",
    });
  }

  for (const x of findSelfIntersections(o)) {
    issues.push({
      type: "self_intersection",
      message: "polygon_self_intersects",
      vertexIndices: x.vertexIndices,
      coordinates: [x.point, ...x.vertexIndices.map((i) => o[i])],
      fix: "auto_repair",
    });
  }

  for (const i of findSpikes(o)) {
    issues.push({
      type: "spike",
      message: "polygon_spike_point",
      vertexIndices: [i],
      coordinates: [o[i]],
      fix: "remove_vertex",
    });
  }

  if (polygonAreaSqMeters(o) <= ZERO_AREA_EPSILON_M2) {
    issues.push({
      type: "zero_area",
      message: "polygon_zero_area",
      vertexIndices: o.map((_, i) => i),
      coordinates: o.slice(),
    });
  }

  return issues;
};

// ---------------------------------------------------------------------------
// Repair helpers — each returns a NEW open ring; the caller re-closes/re-renders.
// ---------------------------------------------------------------------------

/** Remove exact + near-duplicate consecutive points. */
export const removeDuplicatePoints = (ring: Pt[]): Pt[] => {
  const o = toOpenRing(ring);
  const out: Pt[] = [];
  for (let i = 0; i < o.length; i++) {
    const prev = out[out.length - 1];
    const isDup =
      prev &&
      (same(prev, o[i]) ||
        (NEAR_DUP_METERS > 0 && distMeters(prev, o[i]) <= NEAR_DUP_METERS));
    if (!isDup) out.push(o[i]);
  }
  if (out.length > 1 && same(out[0], out[out.length - 1])) out.pop();
  return out;
};

/** Remove a single vertex by index (a spike or a user-chosen bad vertex). */
export const removeVertex = (ring: Pt[], index: number): Pt[] => {
  const o = toOpenRing(ring);
  if (index < 0 || index >= o.length) return o;
  return o.filter((_, i) => i !== index);
};

/**
 * Untwist a self-intersecting ring with no extra dependency: for each crossing,
 * reversing the sub-path between the two crossing edges removes a classic
 * bow-tie/figure-8 (the common hand-drawn case). Degrades gracefully on
 * genuinely tangled rings (returns the best result it reached).
 */
export const autoRepairSelfIntersection = (ring: Pt[]): Pt[] => {
  let o = toOpenRing(ring);
  for (let guard = 0; guard < o.length; guard++) {
    const xs = findSelfIntersections(o);
    if (xs.length === 0) break;
    const { edgeA, edgeB } = xs[0];
    const head = o.slice(0, edgeA + 1);
    const middle = o.slice(edgeA + 1, edgeB + 1).reverse();
    const tail = o.slice(edgeB + 1);
    const candidate = [...head, ...middle, ...tail];
    if (
      findSelfIntersections(candidate).length < findSelfIntersections(o).length
    ) {
      o = candidate;
    } else {
      break;
    }
  }
  return o;
};
